import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { orderBy } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { useDebouncedCallback } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useDebouncedCallback'
import { TaskFilterMenuSectionConfig } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/taskFilterMenu/TaskFilterMenu'

import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getClientFullName, searchForClients } from '../../../common/utils/utils'

export type OpenItemsPanel = 'risk_alerts' | 'tasks' | 'client_activity'

type FilterableItemMaybeClient = {
  client: ClientListClientObject | null
  date: string | null
}

/** Name shown on rows that carry no client object, such as TSL tasks. */
const getClientlessName = (item: FilterableItemMaybeClient): string =>
  'clientName' in item && typeof item.clientName === 'string' ? item.clientName.toLowerCase() : ''

const sortItems = <T extends FilterableItemMaybeClient>(
  items: T[],
  sort: OpenItemsSortOrder,
  isPreferredNameEnabled: boolean,
): T[] => {
  const getName = (item: T) =>
    item.client ? getClientFullName(item.client, isPreferredNameEnabled).toLowerCase() : getClientlessName(item)
  switch (sort) {
    case OpenItemsSortOrder.NEWEST:
      return orderBy(items, (item) => (item.date ? new Date(item.date).getTime() : 0), 'desc')
    case OpenItemsSortOrder.OLDEST:
      return orderBy(items, (item) => (item.date ? new Date(item.date).getTime() : 0), 'asc')
    case OpenItemsSortOrder.NAME_ASC:
      return orderBy(items, getName, 'asc')
    case OpenItemsSortOrder.NAME_DESC:
      return orderBy(items, getName, 'desc')
  }
}

export enum OpenItemsSortOrder {
  NEWEST = 'newest',
  OLDEST = 'oldest',
  NAME_ASC = 'nameAsc',
  NAME_DESC = 'nameDesc',
}

export const useOpenItemsFilterSections = (
  panel: OpenItemsPanel,
  initialSort: OpenItemsSortOrder = OpenItemsSortOrder.NEWEST,
) => {
  const intl = useIntl()
  const { isPreferredNameEnabled } = useFlags()
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<OpenItemsSortOrder>(initialSort)

  const trackSearchDebounced = useDebouncedCallback(() => {
    track({
      event: mixpanelEvents.OPEN_ITEMS_FILTER_CHANGE,
      properties: { panel, filter: 'search' },
    })
  }, 500)

  const sharedSections: TaskFilterMenuSectionConfig[] = [
    {
      type: 'text',
      label: <FormattedMessage defaultMessage='Search' description='Open items filter section label' />,
      placeholder: intl.formatMessage({
        defaultMessage: 'Search by name or Lyra ID',
        description: 'Open items search placeholder',
      }),
      value: search,
      onChange: (value: string) => {
        setSearch(value)
        if (value.trim().length > 0) {
          trackSearchDebounced()
        }
      },
    },
    {
      type: 'radio',
      label: <FormattedMessage defaultMessage='Sort' description='Open items filter section label' />,
      options: [
        {
          id: OpenItemsSortOrder.NEWEST,
          text: intl.formatMessage({ defaultMessage: 'Most recent', description: 'Sort option' }),
        },
        {
          id: OpenItemsSortOrder.OLDEST,
          text: intl.formatMessage({ defaultMessage: 'Oldest', description: 'Sort option' }),
        },
        {
          id: OpenItemsSortOrder.NAME_ASC,
          text: intl.formatMessage({ defaultMessage: 'Client name alphabetical', description: 'Sort option' }),
        },
        {
          id: OpenItemsSortOrder.NAME_DESC,
          text: intl.formatMessage({ defaultMessage: 'Client name reverse alphabetical', description: 'Sort option' }),
        },
      ],
      value: sort,
      defaultValue: initialSort,
      onChange: (value) => {
        const next = (value as OpenItemsSortOrder) ?? initialSort
        setSort(next)
        track({
          event: mixpanelEvents.OPEN_ITEMS_FILTER_CHANGE,
          properties: { panel, filter: 'sort', value: next },
        })
      },
    },
  ]

  const applyFilters = useCallback(
    <T extends FilterableItemMaybeClient>(items: T[]) => {
      const trimmedSearch = search.trim()
      const searched = trimmedSearch
        ? (() => {
            const itemsWithClient = items.filter((item): item is T & { client: ClientListClientObject } =>
              Boolean(item.client),
            )
            const matchingIds = new Set(
              searchForClients({
                clients: itemsWithClient.map((item) => item.client),
                searchValue: trimmedSearch,
              }).map((c) => c.id),
            )
            const lowerSearch = trimmedSearch.toLowerCase()
            const matchedByClientName = items.filter(
              (item) => !item.client && getClientlessName(item).includes(lowerSearch),
            )
            return [...itemsWithClient.filter((item) => matchingIds.has(item.client.id)), ...matchedByClientName]
          })()
        : items
      return sortItems(searched, sort, isPreferredNameEnabled)
    },
    [search, sort, isPreferredNameEnabled],
  )

  const clearSharedFilters = () => {
    setSearch('')
    setSort(initialSort)
  }

  return { sharedSections, clearSharedFilters, applyFilters }
}
